import { useEffect, useRef, useState } from 'react';

import type {
  FailureBreakdownResponse,
  FailureCategoryR,
  FilterKey,
  StatusCounts,
} from '../api/types';
import { cn } from '../lib/classnames';
import { computeReviewBreakdown } from '../lib/reviewBreakdown';
import { BreakdownSheet } from './BreakdownSheet';
import './SummaryCells.css';

interface Cell {
  key: FilterKey;
  label: string;
  count: number;
  className?: 'info' | 'good';
}

/** Top summary strip of the Feed screen (redesign).
 *
 *  Four big counters double as the state filter — tapping one filters
 *  the feed and gets a thin underline. The fifth, «к разбору», is NOT a
 *  filter: it's a soft-ochre counter that drops a compact popover down
 *  from under itself (variant B). Its value is service_failed + other
 *  from ``/stats/failure-breakdown`` (via computeReviewBreakdown) — the
 *  SAME number the popover header shows, so they can't drift. State
 *  counts come from ``/api/v1/status``; dashes («—») show while the
 *  first request is in flight.
 */
export function SummaryCells({
  counts,
  breakdown,
  activeFilter,
  onCellClick,
  onReviewDrill,
}: {
  counts: StatusCounts | undefined;
  /** ``/stats/failure-breakdown`` — drives the «к разбору» counter and
   *  the popover. */
  breakdown: FailureBreakdownResponse | undefined;
  /** Currently-active feed filter — drives the underline indicator. */
  activeFilter: FilterKey;
  onCellClick: (filter: FilterKey) => void;
  /** «К разбору» drill-in — navigate the feed to a
   *  ``?failure_category=…`` list (one bucket, or the combined
   *  service_failed+other set from the popover header). Closes the
   *  popover after navigating. The popover itself stays a quick
   *  summary; this just makes it actionable. */
  onReviewDrill: (categories: FailureCategoryR[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);

  // Close the popover on outside-click and Esc. Clicks on the trigger
  // itself are excluded so its own toggle isn't double-fired.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (popRef.current?.contains(t)) return;
      if (triggerRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const review = computeReviewBreakdown(breakdown);

  const cells: Cell[] = [
    { key: 'all', label: 'всего', count: counts?.total ?? 0 },
    { key: 'pending', label: 'ждут', count: counts?.pending ?? 0 },
    { key: 'active', label: 'активны', count: counts?.submitted ?? 0, className: 'info' },
    { key: 'done', label: 'готово', count: counts?.done ?? 0, className: 'good' },
  ];

  return (
    <div className="feed-summary">
      {cells.map((c) => (
        <button
          key={c.key}
          type="button"
          className={cn('summary-cell', activeFilter === c.key && 'active')}
          aria-pressed={activeFilter === c.key}
          onClick={() => onCellClick(c.key)}
          aria-label={`фильтр: ${c.label}`}
        >
          <div className={`num${c.className ? ' ' + c.className : ''}`}>
            {counts == null ? '—' : c.count}
          </div>
          <div className="label">{c.label}</div>
        </button>
      ))}

      {/* «к разбору» — soft ochre, drops the breakdown popover down from
          under itself (not a filter). Chevron indicates open/closed. */}
      <button
        ref={triggerRef}
        type="button"
        className={cn('summary-cell', 'review', open && 'review-open')}
        onClick={() => setOpen((o) => !o)}
        data-testid="summary-review"
        aria-label="разбор провалов"
        aria-expanded={open}
      >
        <div className="num review">
          {breakdown == null ? '—' : `${review.inCount} !`}
        </div>
        <div className="label">
          к разбору
          <span className="chev" aria-hidden>
            {open ? '▴' : '▾'}
          </span>
        </div>
      </button>

      <BreakdownSheet
        ref={popRef}
        breakdown={breakdown}
        open={open}
        onDrill={(categories) => {
          setOpen(false);
          onReviewDrill(categories);
        }}
      />
    </div>
  );
}
